'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Heart, X } from 'lucide-react';
import { useAuthStore } from '@/store/useAuthStore';
import { mockApi } from '@/lib/mockApi';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/toast';
import type { Product } from '@/types';

export function WishlistPage() {
  const { user } = useAuthStore();
  const { addToast } = useToast();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadWishlist();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function loadWishlist() {
    if (!user) return;
    setLoading(true);
    try {
      const response = await api.wishlist.getWishlist();
      setProducts(response.products);
    } catch (error) {
      console.error('Failed to load wishlist:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleRemove = async (productId: string) => {
    if (!user) return;
    try {
      const response = await api.wishlist.removeFromWishlist(productId);
      setProducts(response.products);
      addToast({ title: 'Устгасан', description: 'Бараа хүслийн жагсаалтаас устгагдлаа' });
    } catch (error: any) {
      addToast({ title: 'Алдаа', description: error.message || 'Бараа устгахэд алдаа гарлаа', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-64 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-3xl font-bold mb-6">Миний хүслийн жагсаалт</h1>
      
      {products.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">Таны хүслийн жагсаалт хоосон байна</p>
          <Link href="/">
            <Button>Худалдаа үргэлжлүүлэх</Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {products.map((product) => (
            <Card key={product.id} className="hover:shadow-lg transition-shadow relative">
              <button
                onClick={() => handleRemove(product.id)}
                className="absolute top-2 right-2 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100"
              >
                <X className="h-4 w-4 text-red-600" />
              </button>
              <Link href={`/product/${product.id}`}>
                <div className="aspect-square relative overflow-hidden rounded-t-lg">
                  <img
                    src={product.images[0]?.url || '/placeholder.png'}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-sm mb-2 line-clamp-2">{product.title}</h3>
                  <div className="flex items-center gap-2">
                    {product.salePrice ? (
                      <>
                        <span className="text-lg font-bold text-blue-600">
                          {formatCurrency(product.salePrice)}
                        </span>
                        <span className="text-sm text-gray-500 line-through">
                          {formatCurrency(product.price)}
                        </span>
                      </>
                    ) : (
                      <span className="text-lg font-bold text-blue-600">
                        {formatCurrency(product.price)}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Link>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

